<div class="wrapper d-flex align-items-stretch">
  <!-- Left Sidebar with Categories -->
  <ozm-sidebar></ozm-sidebar>

  <!-- Page Content -->
  <div id="content" class="p-4 p-md-5">
    <div class="top-wr">
      <ozm-header></ozm-header>
      
      <div class="container">
        <!-- Page Header -->
        <div class="page-header">
          <button class="btn-back" (click)="goBack()">
            <i class="fas fa-arrow-left"></i> Back
          </button>
          <h1>Request Cashback</h1>
          <p class="subtitle">Submit your purchase details to claim cashback</p>
        </div>

        <div class="content-grid">
          <!-- Request Form -->
          <div class="request-form-card">
            <h2><i class="fas fa-plus-circle"></i> New Cashback Request</h2>
            
            <!-- Success Message -->
            <div *ngIf="submitSuccess" class="alert alert-success">
              <i class="fas fa-check-circle"></i>
              Your cashback request has been submitted successfully! 
              It will be reviewed by our team and you'll be notified once approved.
            </div>

            <!-- Error Message -->
            <div *ngIf="submitError" class="alert alert-danger">
              <i class="fas fa-exclamation-circle"></i>
              {{ submitError }}
            </div>

            <!-- Form -->
            <form [formGroup]="cashbackForm" (ngSubmit)="onSubmit()">
              <!-- Application/Store Name Dropdown -->
              <div class="form-group">
                <label for="application">
                  Select Your Favorite App <span class="required">*</span>
                </label>
                
                <!-- Show loading spinner while apps load -->
                <div *ngIf="loadingApps" class="loading-apps-container">
                  <span class="spinner-border spinner-border-sm"></span> Loading your favorite apps...
                </div>
                
                <!-- Dropdown when apps are loaded -->
                <select 
                  *ngIf="!loadingApps && applications.length > 0"
                  id="application"
                  class="form-control app-select"
                  formControlName="application"
                  [class.is-invalid]="isFieldInvalid('application')">
                  <option value="">-- Select an Application --</option>
                  <option *ngFor="let app of applications" [value]="app.name">
                    {{ app.name }}
                  </option>
                </select>
                
                <!-- Message when no favorite apps -->
                <div *ngIf="!loadingApps && applications.length === 0" class="no-apps-message">
                  <i class="fas fa-info-circle"></i>
                  <span>No favorite apps found. Please add apps to your favorites first.</span>
                  <a routerLink="/" class="add-apps-link">Browse Apps</a>
                </div>
                
                <div *ngIf="isFieldInvalid('application')" class="invalid-feedback d-block">
                  {{ getFieldError('application') }}
                </div>
              </div>

              <!-- Amount -->
              <div class="form-group">
                <label for="amount">
                  Amount Requested ($) <span class="required">*</span>
                </label>
                  <input 
                    type="number" 
                    id="amount"
                    class="form-control"
                    formControlName="amount"
                    placeholder="0.00"
                    step="0.01"
                    min="0.01"
                    [class.is-invalid]="isFieldInvalid('amount')">
                <div *ngIf="isFieldInvalid('amount')" class="invalid-feedback d-block">
                  {{ getFieldError('amount') }}
                </div>
                <small class="form-text text-muted">
                  Enter the cashback amount you're requesting. Admin will review and may approve a different amount.
                </small>
              </div>

              <!-- Order Date -->
              <div class="form-group">
                <label for="orderDate">
                  Order / Transaction Date <span class="required">*</span>
                </label>
                <input 
                  type="date" 
                  id="orderDate"
                  class="form-control"
                  formControlName="orderDate"
                  [class.is-invalid]="isFieldInvalid('orderDate')">
                <div *ngIf="isFieldInvalid('orderDate')" class="invalid-feedback">
                  {{ getFieldError('orderDate') }}
                </div>
              </div>

              <!-- Description -->
              <div class="form-group">
                <label for="description">Description (Optional)</label>
                <textarea 
                  id="description"
                  class="form-control"
                  formControlName="description"
                  placeholder="Add any additional details about your purchase..."
                  rows="3"
                  [class.is-invalid]="isFieldInvalid('description')">
                </textarea>
                <div *ngIf="isFieldInvalid('description')" class="invalid-feedback">
                  {{ getFieldError('description') }}
                </div>
              </div>

              <!-- Image Upload -->
              <div class="form-group">
                <label>Upload Proof (Receipt/Screenshot)</label>
                <div class="image-upload-area" 
                     [class.has-image]="imagePreview"
                     (click)="fileInput.click()">
                  
                  <input 
                    #fileInput
                    type="file"
                    accept="image/*"
                    (change)="onFileSelected($event)"
                    style="display: none;">
                  
                  <div *ngIf="!imagePreview" class="upload-placeholder">
                    <i class="fas fa-cloud-upload-alt"></i>
                    <p>Click to upload image</p>
                    <small>JPG, PNG, GIF up to 5MB</small>
                  </div>
                  
                  <div *ngIf="imagePreview" class="image-preview-container">
                    <img [src]="imagePreview" alt="Preview" class="image-preview">
                    <button type="button" class="btn-remove-image" (click)="removeImage(); $event.stopPropagation()">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>
                <small class="form-text text-muted">
                  Upload a screenshot or photo of your receipt/order confirmation to speed up approval.
                </small>
              </div>

              <!-- Submit Button -->
              <div class="form-actions">
                <button 
                  type="submit" 
                  class="btn-submit"
                  [disabled]="loading || cashbackForm.invalid">
                  <span *ngIf="loading" class="spinner-border spinner-border-sm me-2"></span>
                  <i *ngIf="!loading" class="fas fa-paper-plane"></i>
                  {{ loading ? 'Submitting...' : 'Submit Request' }}
                </button>
              </div>
            </form>
          </div>

          <!-- My Requests -->
          <div class="my-requests-card">
            <h2><i class="fas fa-list"></i> My Requests</h2>
            
            <!-- Loading -->
            <div *ngIf="loadingRequests" class="loading-state">
              <div class="spinner-border text-primary"></div>
              <p>Loading your requests...</p>
            </div>

            <!-- Empty State -->
            <div *ngIf="!loadingRequests && myRequests.length === 0" class="empty-state">
              <i class="fas fa-inbox"></i>
              <p>No cashback requests yet</p>
              <small>Submit your first request using the form</small>
            </div>

            <!-- Requests List -->
            <div *ngIf="!loadingRequests && myRequests.length > 0" class="requests-list">
              <div *ngFor="let request of myRequests" class="request-item">
                <div class="request-header">
                  <span class="application-name">{{ request.application }}</span>
                  <span class="badge" [ngClass]="getStatusBadgeClass(request.approved)">
                    {{ request.approved | titlecase }}
                  </span>
                </div>
                
                <div class="request-details">
                  <div class="detail-row">
                    <span class="label">Requested:</span>
                    <span class="value">${{ request.amount }}</span>
                  </div>
                  <div class="detail-row" *ngIf="request.approvedAmount">
                    <span class="label">Approved:</span>
                    <span class="value approved">${{ request.approvedAmount }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="label">Date:</span>
                    <span class="value">{{ request.orderDate }}</span>
                  </div>
                  <div class="detail-row" *ngIf="request.adminNotes">
                    <span class="label">Notes:</span>
                    <span class="value">{{ request.adminNotes }}</span>
                  </div>
                </div>

                <!-- Show image thumbnail if available -->
                <div *ngIf="request.imageUrl" class="request-image">
                  <img [src]="request.imageUrl" alt="Receipt" (click)="viewImage(request.imageUrl)">
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
